import { Controller } from '@hotwired/stimulus'
import { api } from '../../api'
import type SnackBarController from './snack-bar'

// Reveals a group's type picker, and adds a submission of the picked type as
// soon as one is chosen. The response streams in the new card and a fresh picker.
export default class AccountGroupController extends Controller<HTMLElement> {
  declare accountNumberValue: string
  declare containerIdValue: string
  declare containerSessionIdValue: string
  declare readonly addButtonTarget: HTMLElement
  declare readonly hasSnackBarOutlet: boolean
  declare readonly hasTypePickerTarget: boolean
  declare readonly snackBarOutlet: SnackBarController
  declare readonly typePickerTarget: HTMLElement
  declare readonly typeSelectTarget: HTMLSelectElement

  static outlets = ['snack-bar']
  static targets = ['addButton', 'typePicker', 'typeSelect']
  static values = { accountNumber: String, containerId: String, containerSessionId: String }

  // Public methods

  async addSubmission() {
    const submissionViewId = this.typeSelectTarget.value
    if (!submissionViewId) return

    // Held disabled for the round trip so the same type cannot be sent twice.
    this.typeSelectTarget.disabled = true

    try {
      const response = await api.submissions.addToContainer({
        accountNumber: this.accountNumberValue,
        containerSessionId: this.containerSessionIdValue,
        submissionContainerId: this.containerIdValue,
        submissionViewId,
      })
      // A refusal streams its own alert with a 200, so anything else is a fault.
      if (!response.ok) this.#reportFailure()
    } catch {
      this.#reportFailure()
    } finally {
      this.#hideTypePicker()
    }
  }

  showTypePicker() {
    if (!this.hasTypePickerTarget) return

    this.addButtonTarget.classList.add('hidden')
    this.typePickerTarget.classList.remove('hidden')
    this.typeSelectTarget.focus()
  }

  // Private methods

  #reportFailure() {
    if (this.hasSnackBarOutlet) this.snackBarOutlet.add('The submission could not be added. Please try again.')
  }

  // The picker may have been replaced by the response, so read the targets fresh.
  #hideTypePicker() {
    this.typeSelectTarget.value = ''
    this.typeSelectTarget.disabled = false
    this.typePickerTarget.classList.add('hidden')
    this.addButtonTarget.classList.remove('hidden')
  }
}
